import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { InsumoStat, pctColor } from "../chartUtils";
import { BAR_HOVER_PROPS, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

/* ─── Tooltip ──────────────────────────────────────────────────────────────── */
interface TipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string; payload?: InsumoStat }>;
  label?: string;
}
function CustomTooltip({ active, payload, label }: TipProps) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const orcado    = payload.find((p) => p.name === "Orçado")?.value    ?? 0;
  const realizado = payload.find((p) => p.name === "Realizado")?.value ?? 0;
  const saldo     = orcado - realizado;
  const pct       = orcado > 0 ? (realizado / orcado) * 100 : 0;
  const u = (row?.unit ?? "").trim();
  const fmtQ = (q: number) => {
    const n = formatNumber(q, 3);
    return u ? `${n} ${u}` : n;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 min-w-[240px] max-w-[320px]">
      <p className="text-xs font-bold text-slate-700 mb-2 leading-tight">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">{formatCurrency(item.value, true)}</span>
        </div>
      ))}
      <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">Saldo</span>
          <span className={`font-bold ${saldo >= 0 ? "text-slate-700" : "text-red-600"}`}>
            {formatCurrency(saldo, true)}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">% Executado</span>
          <span className="font-bold" style={{ color: pctColor(pct) }}>
            {pct.toFixed(1)}%
          </span>
        </div>
        {row && (row.qtdOrcada > 0 || row.qtdRealizada > 0) && (
          <>
            <div className="pt-1 border-t border-slate-100 mt-1 space-y-0.5 text-[11px] text-slate-600">
              <div className="flex justify-between gap-2">
                <span>Qtd orçada</span>
                <span className="font-semibold text-slate-800 tabular-nums">{fmtQ(row.qtdOrcada)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span>Qtd realizada</span>
                <span className="font-semibold text-slate-800 tabular-nums">{fmtQ(row.qtdRealizada)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span>Saldo qtd.</span>
                <span className="font-semibold text-slate-800 tabular-nums">{fmtQ(row.saldoQtd)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span>% qtd.</span>
                <span className="font-semibold tabular-nums" style={{ color: pctColor(row.pctQtd) }}>
                  {row.pctQtd.toFixed(1)}%
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Y-axis tick truncado ─────────────────────────────────────────────────── */
const MAX_LABEL = 32;
function YTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  const raw   = payload?.value ?? "";
  const label = raw.length > MAX_LABEL ? raw.slice(0, MAX_LABEL - 1) + "…" : raw;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#64748b" fontSize={10}>
      {label}
    </text>
  );
}

/* ─── Componente ───────────────────────────────────────────────────────────── */
interface Props {
  data:    InsumoStat[];
  loading: boolean;
}

export function TopInsumosChart({ data, loading }: Props) {
  const baseHeight = Math.max(320, data.length * 36 + 64);
  const height = Math.min(baseHeight, 720);

  return (
    <ChartContainer
      title="Top Insumos — Orçado × Realizado"
      subtitle="Os 15 principais insumos por valor orçado"
      loading={loading}
      empty={!loading && data.length === 0}
      emptyMessage="Nenhum insumo encontrado para esta obra."
      height={height}
    >
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 4, right: 16, left: 170, bottom: 4 }}
        barCategoryGap="22%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 10, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => formatCurrency(v, true)}
          width={60}
        />
        <YAxis
          type="category"
          dataKey="description"
          tick={<YTick />}
          axisLine={false}
          tickLine={false}
          width={200}
        />
        <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />
        <Bar {...BAR_HOVER_PROPS} dataKey="orcado" name="Orçado" fill="#3b82f6" radius={[0, 4, 4, 0]} maxBarSize={22}>
          {data.map((_, i) => (
            <Cell key={i} fill="#3b82f6" fillOpacity={0.85} />
          ))}
        </Bar>
        <Bar {...BAR_HOVER_PROPS} dataKey="realizado" name="Realizado" fill="#10b981" radius={[0, 4, 4, 0]} maxBarSize={22} />
      </BarChart>
    </ChartContainer>
  );
}
